import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpDown, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { key: "oaa", label: "OAA", description: "Outs Above Average" },
  { key: "frp", label: "FRP", description: "Fielding Runs Prevented" },
];

function getOaaColor(oaa) {
  if (oaa >= 3) return "text-green-400";
  if (oaa >= 1) return "text-green-300";
  if (oaa === 0) return "text-astros-muted";
  if (oaa >= -1) return "text-red-300";
  return "text-red-400";
}

function getRankBadge(rank, total) {
  if (!rank || !total) return "text-astros-muted";
  const pct = rank / total;
  if (pct <= 0.1) return "text-green-400 font-bold";
  if (pct <= 0.33) return "text-green-300";
  if (pct <= 0.66) return "text-white";
  return "text-red-400";
}

export default function FieldingTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("oaa");
  const [sortDir, setSortDir] = useState("desc");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/stats/fielding`);
        setData(res.data);
      } catch (e) {
        console.error("Error fetching fielding:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const players = data?.players || [];
  const sorted = [...players].sort((a, b) => {
    const av = a[sortBy] ?? 0;
    const bv = b[sortBy] ?? 0;
    return sortDir === "desc" ? bv - av : av - bv;
  });

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === "desc" ? "asc" : "desc");
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  return (
    <div data-testid="fielding-tab">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-6 h-6 text-astros-orange" />
        <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white">FIELDING STATS</h2>
      </div>
      <p className="text-astros-muted text-sm mb-6">
        Baseball Savant fielding metrics for Astros players. OAA = Outs Above Average, FRP = Fielding Runs Prevented. Ranked against all MLB players at the same position.
      </p>

      {/* Sort buttons */}
      <div className="flex gap-3 mb-6">
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => handleSort(opt.key)}
            className={`flex items-center gap-1.5 px-4 py-2 font-bebas text-base tracking-wide transition-colors ${
              sortBy === opt.key
                ? "text-astros-orange border-b-2 border-astros-orange"
                : "text-astros-muted hover:text-white"
            }`}
            data-testid={`fielding-sort-${opt.key}`}
          >
            {opt.label}
            {sortBy === opt.key && (
              <ArrowUpDown size={13} className="text-astros-orange" />
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="fielding-table">
          <thead>
            <tr className="border-b border-astros-border text-astros-muted text-xs uppercase tracking-wider">
              <th className="text-left py-3 pr-2 w-8"></th>
              <th className="text-left py-3 pr-4 font-medium">Player</th>
              <th className="text-center py-3 px-2 font-medium">Pos</th>
              <th
                className="text-center py-3 px-2 font-medium cursor-pointer hover:text-astros-orange transition-colors"
                onClick={() => handleSort("oaa")}
              >
                OAA {sortBy === "oaa" && (sortDir === "desc" ? "▼" : "▲")}
              </th>
              <th className="text-center py-3 px-2 font-medium">OAA Rank</th>
              <th
                className="text-center py-3 px-2 font-medium cursor-pointer hover:text-astros-orange transition-colors"
                onClick={() => handleSort("frp")}
              >
                FRP {sortBy === "frp" && (sortDir === "desc" ? "▼" : "▲")}
              </th>
              <th className="text-center py-3 px-2 font-medium">FRP Rank</th>
              <th className="text-center py-3 px-2 font-medium hidden sm:table-cell">Success Rate</th>
              <th className="text-center py-3 px-2 font-medium hidden md:table-cell">vs Expected</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((player, index) => (
              <motion.tr
                key={player.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="border-b border-astros-border/30 hover:bg-astros-border/20 cursor-pointer transition-colors"
                onClick={() => navigate(`/player/${player.id}`)}
                data-testid={`fielding-player-${player.id}`}
              >
                <td className="py-3 pr-2">
                  <img
                    src={player.photo_url}
                    alt={player.name}
                    className="w-8 h-8 object-cover bg-astros-navy"
                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=32&h=32&fit=crop"; }}
                  />
                </td>
                <td className="py-3 pr-4">
                  <div className="flex flex-col">
                    <span className="text-white font-semibold text-sm">{player.name}</span>
                    <span className="text-astros-muted text-xs">
                      <span className="text-astros-orange font-bold">#{player.jersey_number || "00"}</span>
                    </span>
                  </div>
                </td>
                <td className="text-center py-3 px-2">
                  <span className="text-xs font-bold bg-astros-border/40 text-white px-2 py-0.5 rounded">
                    {player.position}
                  </span>
                </td>
                <td className={`text-center py-3 px-2 font-bold text-base ${getOaaColor(player.oaa)}`}>
                  {player.oaa > 0 ? "+" : ""}{player.oaa}
                </td>
                <td className="text-center py-3 px-2">
                  {player.oaa_rank > 0 ? (
                    <span className={`text-xs ${getRankBadge(player.oaa_rank, player.total_at_position)}`}>
                      #{player.oaa_rank}<span className="text-astros-muted">/{player.total_at_position}</span>
                    </span>
                  ) : (
                    <span className="text-xs text-astros-muted">-</span>
                  )}
                </td>
                <td className={`text-center py-3 px-2 font-bold text-base ${getOaaColor(player.frp)}`}>
                  {player.frp > 0 ? "+" : ""}{player.frp}
                </td>
                <td className="text-center py-3 px-2">
                  {player.frp_rank > 0 ? (
                    <span className={`text-xs ${getRankBadge(player.frp_rank, player.total_at_position)}`}>
                      #{player.frp_rank}<span className="text-astros-muted">/{player.total_at_position}</span>
                    </span>
                  ) : (
                    <span className="text-xs text-astros-muted">-</span>
                  )}
                </td>
                <td className="text-center py-3 px-2 text-white text-xs hidden sm:table-cell">
                  {player.success_rate || "-"}
                </td>
                <td className="text-center py-3 px-2 hidden md:table-cell">
                  <span className={`text-xs font-semibold ${
                    player.success_rate_diff?.startsWith("-") ? "text-red-400" :
                    player.success_rate_diff?.startsWith("0") ? "text-astros-muted" : "text-green-400"
                  }`}>
                    {player.success_rate_diff || "-"}
                  </span>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {players.length === 0 && (
        <p className="text-astros-muted text-center py-8">No fielding data available yet</p>
      )}
    </div>
  );
}
